import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Rendered instead of the default panel, e.g. an inline fallback for one widget. */
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time exceptions so one broken component cannot blank the whole application.
 *
 * Still a class component: React has no hook equivalent of componentDidCatch, and this is the
 * only place in the codebase that needs one.
 *
 * Note what it does NOT catch - event handlers, async callbacks and anything outside the
 * render path. Those surface through the toast layer instead.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Phase 10 forwards this to the audit/monitoring pipeline with the correlation ID.
    console.error('Unhandled render error', error, info.componentStack);
  }

  private readonly handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }
    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={this.handleReset}>
              Try again
            </Button>
          }
        >
          <AlertTitle>Something went wrong</AlertTitle>
          <Typography variant="body2">
            This screen failed to render. The rest of the application is unaffected.
          </Typography>
          {import.meta.env.DEV && (
            <Typography variant="caption" component="pre" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {error.message}
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }
}
